// PDA Zone Types

import type { BadgeVariant } from '@/components/common/Badge';

export type PdaZoneStatus = 'pending' | 'assigned' | 'in_progress' | 'complete' | 'needs_review';

export interface PdaZoneSet {
  id: string;
  tenant_id: string;
  incident_id?: string;
  name: string;
  source_filename?: string;
  source_storage_key?: string;
  grid_cell_size_meters: number;
  boundary?: GeoJSON.GeoJSON;
  is_template?: boolean;
  source_template_id?: string;
  created_by: string;
  created_by_name?: string;
  incident_name?: string;
  zone_count?: number;
  zones_complete?: number;
  created_at: string;
  updated_at: string;
  /** Background zone generation: 'queued' / 'generating' while a job runs. */
  generation_status?: 'idle' | 'queued' | 'generating' | 'failed';
  generation_error?: string | null;
  generation_started_at?: string | null;
  generation_finished_at?: string | null;
  generation_summary?: Record<string, unknown> | null;
}

export interface PdaZone {
  id: string;
  tenant_id: string;
  zone_set_id: string;
  zone_number: number;
  name?: string;
  boundary: GeoJSON.GeoJSON;
  status: PdaZoneStatus;
  assigned_to?: string;
  assigned_to_name?: string;
  zone_set_name?: string;
  coverage_percent: number;
  grid_cells_total: number;
  grid_cells_visited: number;
  notes?: string;
  created_at: string;
  updated_at: string;
  /** Zones generated from the road network; null for drawn or uploaded zones. */
  estimated_minutes?: number | null;
  /** NUMERIC: pg sends it as a string. */
  road_km?: number | string | null;
  housing_units?: number | null;
  /** Only on GET /zones/:id; lists leave it out. */
  road_geometry?: GeoJSON.MultiLineString | null;
  /** NUMERIC: pg sends it as a string. */
  road_coverage_percent?: number | string | null;
}

export interface GpsBreadcrumb {
  id: string;
  zone_id: string;
  user_id: string;
  lat: number;
  lng: number;
  accuracy?: number;
  altitude?: number;
  speed?: number;
  heading?: number;
  recorded_at: string;
  batch_id?: string;
}

export interface BreadcrumbInput {
  lat: number;
  lng: number;
  accuracy?: number;
  altitude?: number;
  speed?: number;
  heading?: number;
  recorded_at: string;
}

export interface CoverageStats {
  zone_id: string;
  coverage_percent: number;
  grid_cells_total: number;
  grid_cells_visited: number;
  breadcrumb_count: number;
  /** Share of the zone's roads driven; null for zones without road data. */
  road_coverage_percent?: number | null;
}

export interface ZoneDependencies {
  breadcrumb_count: number;
  assessment_count: number;
  zone_ids_with_data: string[];
}

export const ZONE_STATUS_CONFIG: Record<PdaZoneStatus, { label: string; bgColor: string; textColor: string; variant: BadgeVariant }> = {
  pending: { label: 'Pending', bgColor: 'bg-gray-100 dark:bg-gray-800', textColor: 'text-gray-700 dark:text-gray-300', variant: 'neutral' },
  assigned: { label: 'Assigned', bgColor: 'bg-primary-100 dark:bg-primary-900/30', textColor: 'text-primary-700 dark:text-primary-300', variant: 'info' },
  in_progress: { label: 'In Progress', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30', textColor: 'text-yellow-700 dark:text-yellow-300', variant: 'warning' },
  complete: { label: 'Complete', bgColor: 'bg-green-100 dark:bg-green-900/30', textColor: 'text-green-700 dark:text-green-300', variant: 'success' },
  needs_review: { label: 'Needs Review', bgColor: 'bg-orange-100 dark:bg-orange-900/30', textColor: 'text-orange-700 dark:text-orange-300', variant: 'orange' },
};
